import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCreditProfileCompletion } from "./useShipperCreditProfile";

/**
 * Read-only detector for the Shipper "Getting Started" checklist.
 *
 * SAFETY: this hook is completely decoupled from the credit/network/
 * quote/post/dispatch flows. It only issues SELECT count reads against
 * existing tables. No writes, no mutations, no realtime subscriptions,
 * no imports from flow hooks/components. It cannot affect those flows.
 *
 * Refetches automatically on:
 *   - mount (covers nav-back to /shipper-dashboard)
 *   - window focus / tab visibility change (covers app-switching back)
 */
export interface GettingStartedProgress {
  creditProfile: boolean;
  network: boolean;
  quote: boolean;
  load: boolean;
  dispatch: boolean;
}

const EMPTY: GettingStartedProgress = {
  creditProfile: false,
  network: false,
  quote: false,
  load: false,
  dispatch: false,
};

export function useShipperGettingStartedProgress(
  userId: string | null | undefined,
) {
  const [progress, setProgress] = useState<GettingStartedProgress>(EMPTY);
  const [loaded, setLoaded] = useState(false);

  const check = useCallback(async () => {
    if (!userId) {
      setProgress(EMPTY);
      setLoaded(true);
      return;
    }

    // Resolve shipper's company id (read-only lookup).
    const { data: cu } = await supabase
      .from("company_users")
      .select("company_id")
      .eq("user_id", userId)
      .eq("is_active", true)
      .limit(1)
      .maybeSingle();
    const companyId = (cu?.company_id as string | null) ?? null;

    // ── 1. Credit Profile — read row, run through pure completion helper.
    const creditPromise = companyId
      ? supabase
          .from("shipper_credit_profiles")
          .select("*")
          .eq("company_id", companyId)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null } as const);

    // ── 2. Network — count accepted brokers across shipper's networks.
    //     Two-step: get network ids, then count accepted broker rows.
    const networkPromise = (async () => {
      const { data: nets } = await supabase
        .from("shipper_private_networks" as any)
        .select("id")
        .eq("shipper_user_id", userId);
      const ids = ((nets as any[]) || []).map((r) => r.id);
      if (ids.length === 0) return 0;
      const { count } = await supabase
        .from("shipper_network_brokers" as any)
        .select("id", { count: "exact", head: true })
        .in("network_id", ids)
        .eq("status", "accepted");
      return count ?? 0;
    })();

    // ── 3. Get a Quote — direct_loads(post_type='quote') OR legacy RFQ.
    const quoteLoadPromise = supabase
      .from("direct_loads" as any)
      .select("id", { count: "exact", head: true })
      .eq("created_by", userId)
      .eq("post_type", "quote")
      .is("deleted_at", null);

    const legacyQuotePromise = companyId
      ? supabase
          .from("direct_quote_requests")
          .select("id", { count: "exact", head: true })
          .eq("shipper_company_id", companyId)
      : Promise.resolve({ count: 0 } as any);

    // ── 4. Post a Load — non-quote direct_loads.
    const loadPromise = supabase
      .from("direct_loads" as any)
      .select("id", { count: "exact", head: true })
      .eq("created_by", userId)
      .is("deleted_at", null)
      .neq("post_type", "quote");

    // ── 5. Dispatch — fetch shipper load ids, then count dispatches.
    const dispatchPromise = (async () => {
      const { data: loads } = await supabase
        .from("direct_loads" as any)
        .select("id")
        .eq("created_by", userId)
        .is("deleted_at", null);
      const loadIds = ((loads as any[]) || []).map((l) => l.id);
      if (loadIds.length === 0) return 0;
      const { count } = await supabase
        .from("direct_dispatches" as any)
        .select("id", { count: "exact", head: true })
        .in("direct_load_id", loadIds);
      return count ?? 0;
    })();

    const [creditRes, networkCount, quoteLoadRes, legacyQuoteRes, loadRes, dispatchCount] =
      await Promise.all([
        creditPromise,
        networkPromise,
        quoteLoadPromise,
        legacyQuotePromise,
        loadPromise,
        dispatchPromise,
      ]);

    const creditComplete = getCreditProfileCompletion(
      (creditRes as any)?.data ?? null,
    ).isComplete;
    const quoteCount =
      ((quoteLoadRes as any)?.count ?? 0) + ((legacyQuoteRes as any)?.count ?? 0);

    setProgress({
      creditProfile: creditComplete,
      network: networkCount > 0,
      quote: quoteCount > 0,
      load: ((loadRes as any)?.count ?? 0) > 0,
      dispatch: dispatchCount > 0,
    });
    setLoaded(true);
  }, [userId]);

  useEffect(() => {
    check();
  }, [check]);

  // Refetch when the user returns to the tab / window (covers nav-back
  // from Post/Dispatch/Network/Credit pages within the SPA is already
  // handled by mount, but focus/visibility covers app-switch cases too).
  useEffect(() => {
    const onFocus = () => check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  return { progress, loaded, refresh: check };
}
